'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

type ClipboardButtonProps = {
  textToCopy: string;
  className?: string;
  textSpanClassName?: string;
  feedbackClassName?: string;
  feedbackText?: string;
  feedbackDuration?: number;
  iconDirection?: 'left' | 'right';
  icon?: React.ReactNode;
  children?: React.ReactNode;
};

function ClipboardButton({
  textToCopy,
  className,
  feedbackClassName,
  textSpanClassName,
  feedbackText,
  feedbackDuration = 1500,
  iconDirection = 'right',
  icon,
  children,
}: Readonly<ClipboardButtonProps>) {
  const [copied, setCopied] = useState(false);
  const t = useTranslations('ActivationCodeView');

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), feedbackDuration);
  };

  return (
    <CopyToClipboard text={textToCopy} onCopy={handleCopy}>
      <button
        type="button"
        className={twMerge(
          'relative flex items-center gap-2 rounded-full border border-solid border-neutral-20 px-5 py-2 duration-100 ease-in hover:bg-neutral-10',
          className,
        )}
        aria-label={t('Copy')}
      >
        {iconDirection === 'left' &&
          (icon || <ClipboardDocumentListIcon className="h-6 w-6 text-neutral-700" />)}

        <span className={twMerge('text-lg font-bold sm:text-xl', textSpanClassName)}>
          {children || textToCopy}
        </span>

        {iconDirection === 'right' &&
          (icon || <ClipboardDocumentListIcon className="h-6 w-6 text-neutral-700" />)}

        {/* Overlay Feedback */}
        <Transition
          as={React.Fragment}
          show={copied}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={twMerge(
              'absolute inset-0 flex items-center justify-center rounded-full bg-white text-neutral-100',
              feedbackClassName,
            )}
          >
            {feedbackText || t('Copied')}
          </div>
        </Transition>
      </button>
    </CopyToClipboard>
  );
}

export default ClipboardButton;
